import { useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './Contact.module.css'
import { sendContactEmail } from '../services/emailService'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Send the contact form data to the email server
      const response = await sendContactEmail(formData)

      if (response.success) {
        console.log('Contact form submitted successfully:', response.reference)
        setSubmitStatus('success')

        // Clear the form on success
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          message: ''
        })
      } else {
        console.error('Contact form submission failed:', response.error)
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      // Reset status message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className={styles.contactPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <div className={styles.blob1} />
          <div className={styles.blob2} />
        </div>
        <div className="container">
          <Link to="/boxos" className={styles.backLink}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16 10H4M4 10L10 16M4 10L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Home
          </Link>
          <h1 className={styles.heroTitle}>
            Join <span className="gradient-text">the waitlist.</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Box Editor is in early access. We're onboarding developers who care about code quality — not just code speed. Tell us about your codebase and we'll be in touch.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className={styles.formSection}>
        <div className="container">
          <div className={styles.formContainer}>
            {/* Left Side - Info */}
            <div className={styles.infoSide}>
              <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>What we're looking for</h3>
                <ul className={styles.infoList}>
                  <li className={styles.infoItem}>
                    <span className={styles.infoIcon}>→</span>
                    <div>
                      <strong>Developers with a standard</strong>
                      <p>People who've already felt the cost of suggestions that technically compile but don't fit</p>
                    </div>
                  </li>
                  <li className={styles.infoItem}>
                    <span className={styles.infoIcon}>→</span>
                    <div>
                      <strong>Real codebases</strong>
                      <p>Active work — not toy projects. The messier and more context-dependent, the more useful the feedback</p>
                    </div>
                  </li>
                  <li className={styles.infoItem}>
                    <span className={styles.infoIcon}>→</span>
                    <div>
                      <strong>Honest feedback</strong>
                      <p>We review every request ourselves. If it's not right yet, we'd rather know</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className={styles.contactInfo}>
                <h3 className={styles.infoTitle}>Get in Touch</h3>
                <div className={styles.contactDetails}>
                  <div className={styles.contactItem}>
                    <span className={styles.contactIcon}>→</span>
                    <a href="mailto:contact@boxos.dev">contact@boxos.dev</a>
                  </div>
                  {/* <div className={styles.contactItem}>
                    <span className={styles.contactIcon}>📞</span>
                    <a href="tel:+1234567890">+1 (234) 567-890</a>
                  </div> */}
                </div>
              </div>

              <div className={styles.availabilityNote}>
                <span className={styles.noteIcon}>→</span>
                <p>The waitlist is limited — we review every request</p>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className={styles.formSide}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.label}>
                      Full Name <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className={styles.input}
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={styles.input}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="company" className={styles.label}>
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className={styles.input}
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="projectType" className={styles.label}>
                    What are you working on? <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    className={styles.select}
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select one</option>
                    <option value="new-build">A new build</option>
                    <option value="existing-work">Something existing that needs work</option>
                    <option value="integration">An integration or extension</option>
                    <option value="not-sure">Not sure yet — still figuring it out</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message" className={styles.label}>
                    What's the problem you're trying to solve? <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className={styles.textarea}
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Start wherever makes sense."
                  />
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send message'}
                </button>

                {submitStatus === 'success' && (
                  <div className={styles.successMessage}>
                    Got it. We'll read this carefully and be in touch.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className={styles.errorMessage}>
                    Something didn't go through. You can also reach us directly at contact@boxos.dev
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
